// Interactive TanStack Table host for parsed markdown tables.
import {
    type ColumnDef,
    type RowData,
    type SortingState,
    type Updater,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { trackTableFiltered, trackTableSorted } from "@/lib/analytics";
import { cn } from "@/lib/utils";

import { EditorialInlineText } from "./editorial-inline-text";
import {
    BooleanIconCell,
    CurrencyCell,
    DefaultCell,
    EmDashCell,
    RowCounterCell,
    SourceChipCell,
    StatusBadgeCell,
} from "./chat-table-cells";
import { ChatTableToolbar } from "./chat-table-toolbar";
import { isPureSourceListCell } from "./utils/cell-source-segments";
import {
    detectBooleanColumn,
    detectCurrency,
    detectDate,
    detectEmDash,
    detectRowCounterColumn,
    isStatusColumn,
    parseCurrencyValue,
    parseDateValue,
} from "./utils/cell-detection";
import type {
    CellSpec,
    ChatTableData,
    ColumnType,
} from "./utils/parse-markdown-table";

declare module "@tanstack/react-table" {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> {
        columnType?: ColumnType;
    }
}

function inferColumnType(
    headerText: string,
    rows: CellSpec[][],
    colIndex: number
): ColumnType {
    if (detectRowCounterColumn(headerText)) return "row-counter";

    let hasStatusTag = false;
    let hasBoolTag = false;
    let nonEmptyCount = 0;
    let allCurrencyOrEmDash = true;
    let allDateOrEmDash = true;
    const texts: string[] = [];
    for (const row of rows) {
        const cell = row[colIndex];
        if (cell.tag?.kind === "status") hasStatusTag = true;
        else if (cell.tag?.kind === "bool") hasBoolTag = true;
        if (cell.text === "") continue;
        nonEmptyCount++;
        texts.push(cell.text);
        if (!detectEmDash(cell.text)) {
            // Skip the regex test once a column has been disqualified.
            if (allCurrencyOrEmDash && !detectCurrency(cell.text)) {
                allCurrencyOrEmDash = false;
            }
            if (allDateOrEmDash && !detectDate(cell.text)) {
                allDateOrEmDash = false;
            }
        }
    }

    if (isStatusColumn(headerText) || hasStatusTag) return "status";
    if (hasBoolTag || detectBooleanColumn(headerText, texts)) return "boolean";
    if (nonEmptyCount === 0) return "text";
    if (allCurrencyOrEmDash) return "currency";
    if (allDateOrEmDash) return "date";
    return "text";
}

function isNumericColumn(columnType: ColumnType | undefined): boolean {
    return columnType === "currency";
}

// `row-counter` is bypassed by the cell factory before this is called;
// see the `cell:` definition below. Per-cell source overrides column
// type so a single column can mix pure-source and mixed-source cells.
function pickRenderer(cell: CellSpec, columnType: ColumnType) {
    if (cell.sources?.length) {
        return isPureSourceListCell(cell) ? (
            <SourceChipCell cell={cell} />
        ) : (
            <DefaultCell cell={cell} />
        );
    }
    if (detectEmDash(cell.text)) return <EmDashCell />;
    switch (columnType) {
        case "status":
            return <StatusBadgeCell cell={cell} />;
        case "boolean":
            return <BooleanIconCell cell={cell} />;
        case "currency":
            return <CurrencyCell cell={cell} />;
        default:
            return <DefaultCell cell={cell} />;
    }
}

// Em-dash → undefined for every column type so `sortUndefined: "last"`
// pushes those rows down in both sort directions.
function buildAccessor(columnType: ColumnType, colIndex: number) {
    return (row: CellSpec[]) => {
        const t = row[colIndex].text;
        if (detectEmDash(t)) return undefined;
        switch (columnType) {
            case "currency":
                return parseCurrencyValue(t) ?? undefined;
            case "date":
                return parseDateValue(t) ?? undefined;
            default:
                return t.toLowerCase();
        }
    };
}

// Closes over a pre-lowercased haystack per row and the normalized
// needle so the per-row callback is a single `.includes` lookup. The
// previous shape lowercased every cell text on every keystroke after
// debounce.
function buildGlobalFilterFn(haystacks: string[], needle: string) {
    return (row: { index: number }) => {
        if (!needle) return true;
        return haystacks[row.index].includes(needle);
    };
}

export function ChatTable({
    data,
    chatId: chatIdProp,
}: {
    data: ChatTableData;
    /** Override the `useParams<{ chatId }>` route param (Storybook and tests). */
    chatId?: string;
}) {
    const { chatId: chatIdParam } = useParams<{ chatId: string }>();
    const chatId = chatIdProp ?? chatIdParam ?? null;
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const hasFilteredOnceRef = useRef(false);

    // `\0` separator keeps a needle from matching across cell boundaries
    // (e.g. last char of one cell + first char of the next).
    const haystacks = useMemo(
        () =>
            data.rows.map((row) =>
                row.map((c) => c.text.toLowerCase()).join("\0")
            ),
        [data]
    );
    const normalizedFilter = useMemo(
        () => globalFilter.toLowerCase().trim(),
        [globalFilter]
    );
    const globalFilterFn = useMemo(
        () => buildGlobalFilterFn(haystacks, normalizedFilter),
        [haystacks, normalizedFilter]
    );

    const columnTypes = useMemo<ColumnType[]>(
        () =>
            data.headers.map((header, colIndex) =>
                inferColumnType(header.text, data.rows, colIndex)
            ),
        [data]
    );

    const columns = useMemo<ColumnDef<CellSpec[]>[]>(() => {
        return data.headers.map((header, colIndex) => {
            const columnType = columnTypes[colIndex];
            const enableSorting = columnType !== "row-counter";
            const isNumeric = isNumericColumn(columnType);
            const labelClasses =
                "text-primary text-xs font-bold tracking-wide uppercase";

            return {
                id: String(colIndex),
                accessorFn: buildAccessor(columnType, colIndex),
                meta: { columnType },
                enableSorting,
                // TanStack defaults numeric columns to desc-first;
                // override so every column header sorts asc on first
                // click, matching the rest of the app.
                sortDescFirst: false,
                sortUndefined: enableSorting ? ("last" as const) : undefined,
                header: ({ column }) => {
                    const sorted = column.getIsSorted();
                    // Chevron leads numeric (right-aligned) labels and
                    // trails text labels so digits stay flush to the
                    // column edge in both alignments.
                    const sortIcon =
                        sorted === "asc" ? (
                            <ChevronUp className="h-3 w-3 text-indigo-500" />
                        ) : sorted === "desc" ? (
                            <ChevronDown className="h-3 w-3 text-indigo-500" />
                        ) : (
                            <ChevronsUpDown className="h-3 w-3 opacity-40" />
                        );
                    return enableSorting ? (
                        <Button
                            variant="ghost"
                            onClick={() => column.toggleSorting()}
                            className={cn(
                                "aui-md-th-sort h-auto gap-1 px-0 whitespace-normal hover:bg-transparent",
                                labelClasses,
                                isNumeric && "ms-auto"
                            )}
                        >
                            {isNumeric && sortIcon}
                            <span>
                                <EditorialInlineText text={header.text} />
                            </span>
                            {!isNumeric && sortIcon}
                        </Button>
                    ) : (
                        <span className={labelClasses}>
                            <EditorialInlineText text={header.text} />
                        </span>
                    );
                },
                cell: (ctx) =>
                    columnType === "row-counter" ? (
                        <RowCounterCell rowIndex={ctx.row.index} />
                    ) : (
                        pickRenderer(ctx.row.original[colIndex], columnType)
                    ),
            };
        });
    }, [data, columnTypes]);

    // Resolve the next state up-front and call `setSorting(next)`
    // rather than `setSorting((prev) => ...)`. Updater functions are
    // double-invoked under React 18 StrictMode in dev, which would
    // double-fire the analytics call below.
    const handleSortingChange = (updater: Updater<SortingState>) => {
        const next = typeof updater === "function" ? updater(sorting) : updater;
        setSorting(next);
        if (next.length > 0) {
            const [first] = next;
            trackTableSorted({
                chat_id: chatId,
                column_index: Number(first.id),
                direction: first.desc ? "desc" : "asc",
            });
        }
    };

    // Memoized because the toolbar's debounce effect lists this in its
    // deps; an unmemoized identity would re-arm the 150ms timer on
    // every parent render. Analytics fires only on the empty/non-empty
    // edge; the ref keeps that edge detection out of render state.
    const handleGlobalFilterChange = useCallback(
        (next: string) => {
            setGlobalFilter(next);
            const hasFilter = next.trim().length > 0;
            if (hasFilter !== hasFilteredOnceRef.current) {
                hasFilteredOnceRef.current = hasFilter;
                trackTableFiltered({ chat_id: chatId, has_filter: hasFilter });
            }
        },
        [chatId]
    );

    const table = useReactTable<CellSpec[]>({
        data: data.rows,
        columns,
        state: { sorting, globalFilter },
        onSortingChange: handleSortingChange,
        onGlobalFilterChange: handleGlobalFilterChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        globalFilterFn,
    });

    const filteredRows = table.getRowModel().rows;
    const showEmptyPlaceholder =
        filteredRows.length === 0 && globalFilter.trim() !== "";

    return (
        <div className="aui-md-table-wrapper @container my-4 ml-[calc(-50cqw+50%)] w-[100cqw] px-4">
            <div
                // `overflow-x-auto` is the scroll container the toolbar
                // pins to via `sticky top-0`; rounded chrome clips here.
                className={cn(
                    "mx-auto w-max max-w-full overflow-x-auto",
                    "bg-card rounded-xl border shadow-sm"
                )}
            >
                <ChatTableToolbar
                    title={data.title}
                    rowCountUnfiltered={data.rows.length}
                    columnCount={data.headers.length}
                    data={data}
                    setGlobalFilter={handleGlobalFilterChange}
                />
                <Table className="aui-md-table">
                    <TableHeader className="bg-muted/30">
                        {table.getHeaderGroups().map((hg) => (
                            <TableRow key={hg.id} className="aui-md-tr">
                                {hg.headers.map((h) => {
                                    const sortDir = h.column.getIsSorted();
                                    const ariaSort = h.column.getCanSort()
                                        ? sortDir === "asc"
                                            ? "ascending"
                                            : sortDir === "desc"
                                              ? "descending"
                                              : "none"
                                        : undefined;
                                    const isNumeric = isNumericColumn(
                                        h.column.columnDef.meta?.columnType
                                    );
                                    return (
                                        <TableHead
                                            key={h.id}
                                            className={cn(
                                                "aui-md-th whitespace-normal",
                                                isNumeric && "text-right"
                                            )}
                                            aria-sort={ariaSort}
                                        >
                                            {flexRender(
                                                h.column.columnDef.header,
                                                h.getContext()
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {showEmptyPlaceholder
                            ? null
                            : filteredRows.map((row) => (
                                  <TableRow
                                      key={row.id}
                                      className="aui-md-tr even:bg-muted/80 hover:bg-muted transition-none"
                                  >
                                      {row.getVisibleCells().map((cell) => {
                                          const isNumeric = isNumericColumn(
                                              cell.column.columnDef.meta
                                                  ?.columnType
                                          );
                                          return (
                                              <TableCell
                                                  key={cell.id}
                                                  className={cn(
                                                      "aui-md-td whitespace-normal",
                                                      isNumeric && "text-right"
                                                  )}
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext()
                                                  )}
                                              </TableCell>
                                          );
                                      })}
                                  </TableRow>
                              ))}
                    </TableBody>
                </Table>
                {showEmptyPlaceholder && (
                    <div
                        role="status"
                        className="text-muted-foreground py-12 text-center text-sm"
                    >
                        No matching rows
                    </div>
                )}
            </div>
        </div>
    );
}
